import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import Alert from '../../components/ui/Alert';
import { useAuth } from '../../hooks/useAuth';

import { ROTAS } from '@/routes/routePaths';
import { motion } from 'framer-motion';

// Esquema de validação para o formulário de login
const loginSchema = z.object({
  email: z.string().email('Email inválido.').min(1, 'Email é obrigatório.'),
  senha: z.string().min(1, 'Senha é obrigatória.').min(6, 'A senha deve ter no mínimo 6 caracteres.'),
});

type LoginFormData = z.infer<typeof loginSchema>;

const LoginPage: React.FC = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const [erroLogin, setErroLogin] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (dados: LoginFormData) => {
    setCarregando(true);
    setErroLogin(null);
    try {
      await auth.login(dados.email, dados.senha);
      navigate('/');
    } catch (error: any) {
      setErroLogin(error.message || 'Erro ao fazer login. Verifique suas credenciais.');
    } finally {
      setCarregando(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background principal da página - FIXADO NO FUNDO */}
      <div className="fixed inset-0 z-0">
        <img
          src="/Background-secao2.png"
          alt="Background"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/20"></div>
      </div>

      {/* Card de login */}
      <motion.div
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 bg-background/95 backdrop-blur-sm p-8 rounded-lg shadow-2xl max-w-md w-full border border-primary"
      >
        {/* Background interno do card (opcional) */}
        <div className="absolute inset-0 z-0 rounded-lg overflow-hidden">
          <img
            src="/BG.png"
            alt="Background do card"
            className=" object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-black/5 rounded-lg"></div>
        </div>

        {/* Conteúdo do card */}
        <div className="relative z-20">
          <div className="flex justify-center mb-6">
            <Link to={'/'}>
              <img 
                src={'/clube.png'} 
                alt="TugasCoins Logo" 
                className='h-72 object-contain' 
              />
            </Link>
          </div>
          
          <h2 className="text-3xl font-bold text-center text-text mb-6">
            Bem-vindo de volta!
          </h2>

          {erroLogin && (
            <Alert 
              tipo="error" 
              mensagem={erroLogin} 
              onClose={() => setErroLogin(null)} 
              className="mb-4" 
            />
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-text text-sm font-medium mb-2">
                Email
              </label>
              <Input
                id="email"
                tipo="email"
                placeholder="seu.email@exemplo.com"
                {...register('email')}
                className={`w-full ${errors.email ? 'border-danger' : 'border-primary/20'}`}
              />
              {errors.email && <p className="text-danger text-sm mt-1">{errors.email.message}</p>}
            </div>

            <div>
              <label htmlFor="senha" className="block text-text text-sm font-medium mb-2">
                Senha
              </label>
              <Input
                id="senha"
                tipo="password"
                placeholder="••••••••"
                {...register('senha')}
                className={`w-full ${errors.senha ? 'border-danger' : 'border-primary/20'}`}
              />
              {errors.senha && <p className="text-danger text-sm mt-1">{errors.senha.message}</p>}
            </div>

            <div className="flex justify-end text-sm">
              <Link to="/recuperar-senha" className="text-primary hover:underline">
                Esqueceu a senha?
              </Link>
            </div>

            <Button
              tipo="primary"
              type="submit"
              className="w-full py-3 text-lg font-semibold hover:bg-gradient-to-r hover:from-[#c5a663] hover:via-[#cebc6e] hover:to-[#c5a663]"
              disabled={carregando}
            >
              {carregando ? 'Entrando...' : 'Entrar'}
            </Button>
          </form>

          <div className="mt-6 text-center text-text">
            Não tem uma conta?{' '}
            <Link to={ROTAS.CADASTRO} className="text-primary hover:underline font-medium">
              Crie uma agora!
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default LoginPage;